/**
 * @file App.tsx
 * @brief Root application component — wires providers and the router.
 *
 * @details
 * Provider order (outer → inner):
 *   1. ThemeProvider     (dark/light class on <html>)
 *   2. QueryClientProvider (TanStack Query cache)
 *   3. AuthProvider      (auth state, token bootstrap)
 *   4. RouterProvider    (route tree)
 *
 * Order matters: `AuthProvider` issues HTTP calls during bootstrap, so
 * the QueryClient and Axios client must already be available.
 *
 * @dependencies
 * - @tanstack/react-query
 * - react-router-dom
 *
 * @author DES
 * @date 26-05-2026
 */

import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { DesktopLauncherBridge } from "@/shared/desktop/DesktopLauncherBridge";
import { DesktopScheduleBridge } from "@/features/scheduleReminders/DesktopScheduleBridge";
import { AuthProvider } from "@/shared/auth/AuthContext";
import { ThemeProvider } from "@/shared/theme/ThemeProvider";
import { queryClient } from "@/shared/api/queryClient";
import { Toaster } from "@/components/ui/Toaster";
import { TermsGate } from "@/features/auth/TermsGate";
import { AiConsentHost } from "@/features/ai-consent/AiConsentHost";
import { router } from "@/routes";
import { DesktopNotificationBridge } from "@/features/pushNotifications/DesktopNotificationBridge";
import { DesktopUpdateGate } from "@/shared/desktop/DesktopUpdateGate";

export default function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          {/* GDPR (06-10-2026): asks once for the current Terms + Privacy. */}
          <TermsGate />
          {/* "How VERBLIZR uses AI" (B7, 22-10-2026): before the first recording,
              interpretation or Ask, and on any 403 AI_CONSENT_REQUIRED. */}
          <AiConsentHost />
          <Toaster />
          {/* Desktop app only: the menu-bar icon's quick actions (02-10-2026). */}
          <DesktopLauncherBridge />
          {/* Desktop app only: alarms + Mac calendar for scheduled items (09-10-2026). */}
          <DesktopScheduleBridge />
          {/* Desktop app only: native notifications (ADR-032, 09-10-2026). */}
          <DesktopNotificationBridge />
          {/* Mac app older than MIN_APP_VERSION_MAC → "Please update" (B23 / W19,
              24-10-2026). Also loads GET /api/public/config for every page. */}
          <DesktopUpdateGate />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
